import { Button, CircularProgress, DialogActions, DialogContentText, Divider, Typography } from '@material-ui/core';
import { Dispatch } from '@reduxjs/toolkit';
import React, { Component, Fragment } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AppDispatch, AppState } from 'store';
import { DialogOpenPayloadJobs } from 'store/dialog/dialog.interface';
import { performOperation } from 'store/jobs/jobs.actions';
import { errorMessageIgnore } from 'store/projects/projects.actions';
import { getJobActionSpinnerSelector, getThirdJobActionSpinnerSelector } from 'store/spinner/spinner.selectors';
import { JobsErrorCodeIgnoreDTO, MassOperationRequest } from 'utils/restApplicationClient';
import { CustomDialogButtonContainer, CustomDialogContent } from '../CustomDialog.styled';

interface IProps {
  jobs: DialogOpenPayloadJobs;
  errorMessage: string;
}

interface IDispatchProps {
  performOperation: (payload: MassOperationRequest) => AppDispatch;
  errorMessageIgnore: (payload: JobsErrorCodeIgnoreDTO) => AppDispatch;
}

interface IStateProps {
  jobActionSpinner: boolean;
  thirdJobActionSpinner: boolean;
}

type PropType = IDispatchProps & WithTranslation & IProps & IStateProps;

class ProjectCancelled extends Component<PropType> {
  getText = (): JSX.Element => {
    const { jobs, t, errorMessage } = this.props;

    if (errorMessage === 'errormessage.job.deleted' || errorMessage === 'errormessage.job.projectcancelled') {
      return (
        <Fragment>
          {t(`modal.project${jobs.length > 1 ? 's' : ''}DeletedAction1`)}
          <br />
          {t(`modal.project${jobs.length > 1 ? 's' : ''}CancelledAction2`)}
        </Fragment>
      );
    }
    return (
      <Fragment>
        {t(`modal.project${jobs.length > 1 ? 's' : ''}CancelledAction1`)}
        <br />
        {t(`modal.project${jobs.length > 1 ? 's' : ''}CancelledAction2`)}
      </Fragment>
    );
  };

  handleRemoveClick = (): void => {
    const { performOperation, jobs, errorMessage } = this.props;

    if (errorMessage === 'errormessage.job.deleted') {
      performOperation({
        jobsIds: jobs.map(({ id }) => id),
        operation: 'CANCEL',
      });
    } else {
      performOperation({
        jobsIds: jobs.map(({ id }) => id),
        operation: 'DELETE',
      });
    }
  };

  handleIgnoreClick = (): void => {
    const { errorMessageIgnore, errorMessage, jobs } = this.props;
    errorMessageIgnore({
      jobUUIDs: jobs.map(({ id }) => id),
      errorMessageCode: errorMessage,
    });
  };

  render(): JSX.Element {
    const { t, jobActionSpinner, thirdJobActionSpinner } = this.props;
    const isDisabled = jobActionSpinner || thirdJobActionSpinner;

    return (
      <Fragment>
        <CustomDialogContent>
          <DialogContentText>
            <Typography component="span" color="textPrimary">
              {this.getText()}
            </Typography>
          </DialogContentText>
        </CustomDialogContent>
        <Divider />
        <DialogActions>
          <CustomDialogButtonContainer>
            <Button color="secondary" disabled={isDisabled} variant="contained" onClick={this.handleRemoveClick}>
              {(jobActionSpinner && <CircularProgress size="24px" color="secondary" />) || t('modal.delete')}
            </Button>
            <Button variant="outlined" disabled={isDisabled} onClick={this.handleIgnoreClick}>
              {(thirdJobActionSpinner && <CircularProgress size="24px" />) || t('modal.leave')}
            </Button>
          </CustomDialogButtonContainer>
        </DialogActions>
      </Fragment>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  jobActionSpinner: getJobActionSpinnerSelector(state),
  thirdJobActionSpinner: getThirdJobActionSpinnerSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  performOperation: (payload: MassOperationRequest): AppDispatch => dispatch(performOperation(payload)),
  errorMessageIgnore: (payload: JobsErrorCodeIgnoreDTO): AppDispatch => dispatch(errorMessageIgnore(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ProjectCancelled));
